import { useCallback, useEffect, useRef, useState } from 'react';
import SignaturePad, { type PointGroup } from 'signature_pad';

import { cn } from '@/shared/utils/cn';

import { Button } from './Button';
import { Input } from './Input';
import { TabPills, tabPanelId, tabPillId } from './TabPills';

const SIGNATURE_FILE_NAME = 'firma.png';
const TYPED_SIGNATURE_FONT = '34px "Brush Script MT", "Segoe Script", "Snell Roundhand", cursive';
const TYPED_SIGNATURE_PADDING = 32;
const SIGNATURE_MIME_TYPE = 'image/png';

type SignatureMode = 'draw' | 'type';

const MODE_TABS: readonly { id: SignatureMode; label: string }[] = [
  { id: 'draw', label: 'Dibujar mi firma' },
  { id: 'type', label: 'Escribir mi nombre' },
];

export type SignatureInputProps = {
  id: string;
  /** Firma ya guardada en el formulario: se pinta al montar para no perderla al cambiar de paso. */
  value?: File;
  onChange: (file: File | undefined) => void;
  hasError?: boolean;
  'aria-describedby'?: string;
  /** Recibe el elemento que debe enfocarse cuando el campo queda con error. */
  focusRef?: (element: HTMLElement | null) => void;
};

function toCssSizeCanvas(canvas: HTMLCanvasElement) {
  const output = document.createElement('canvas');
  output.width = canvas.offsetWidth;
  output.height = canvas.offsetHeight;
  output.getContext('2d')?.drawImage(canvas, 0, 0, output.width, output.height);
  return output;
}

function drawTypedName(canvas: HTMLCanvasElement, name: string) {
  const context = canvas.getContext('2d');
  if (!context) return;

  context.font = TYPED_SIGNATURE_FONT;
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillStyle = getComputedStyle(canvas).color;
  context.fillText(
    name,
    canvas.offsetWidth / 2,
    canvas.offsetHeight / 2,
    canvas.offsetWidth - TYPED_SIGNATURE_PADDING,
  );
}

export function SignatureInput({
  id,
  value,
  onChange,
  hasError,
  'aria-describedby': describedBy,
  focusRef,
}: SignatureInputProps) {
  const [mode, setMode] = useState<SignatureMode>('draw');
  const [typedName, setTypedName] = useState('');
  const [isEmpty, setIsEmpty] = useState(true);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const padRef = useRef<SignaturePad | null>(null);
  const strokesRef = useRef<PointGroup[]>([]);
  const restoredImageRef = useRef<HTMLImageElement | null>(null);
  const initialValueRef = useRef(value);
  const modeRef = useRef<SignatureMode>('draw');
  const typedNameRef = useRef('');
  const exportGenerationRef = useRef(0);
  const hasValueRef = useRef(Boolean(value));
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const emitFile = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const generation = ++exportGenerationRef.current;
    toCssSizeCanvas(canvas).toBlob((blob) => {
      if (!blob || generation !== exportGenerationRef.current) return;
      hasValueRef.current = true;
      onChangeRef.current(new File([blob], SIGNATURE_FILE_NAME, { type: SIGNATURE_MIME_TYPE }));
    }, SIGNATURE_MIME_TYPE);
    setIsEmpty(false);
  }, []);

  const emitEmpty = () => {
    exportGenerationRef.current += 1;
    setIsEmpty(true);
    if (!hasValueRef.current) return;
    hasValueRef.current = false;
    onChangeRef.current(undefined);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const styles = getComputedStyle(canvas);
    const pad = new SignaturePad(canvas, {
      penColor: styles.color,
      backgroundColor: styles.backgroundColor,
      minWidth: 0.8,
      maxWidth: 2.4,
    });
    padRef.current = pad;

    const paint = () => {
      pad.clear();
      if (restoredImageRef.current) {
        canvas
          .getContext('2d')
          ?.drawImage(restoredImageRef.current, 0, 0, canvas.offsetWidth, canvas.offsetHeight);
      }
      if (modeRef.current === 'draw') pad.fromData(strokesRef.current, { clear: false });
      else if (typedNameRef.current) drawTypedName(canvas, typedNameRef.current);
    };

    const resize = () => {
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      if (!canvas.offsetWidth) return;
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      canvas.getContext('2d')?.scale(ratio, ratio);
      paint();
    };

    const restore = (file: File) => {
      const url = URL.createObjectURL(file);
      const image = new Image();
      image.onload = () => {
        URL.revokeObjectURL(url);
        restoredImageRef.current = image;
        paint();
        setIsEmpty(false);
      };
      image.onerror = () => URL.revokeObjectURL(url);
      image.src = url;
    };

    const handleStrokeEnd = () => {
      strokesRef.current = pad.toData();
      emitFile();
    };

    resize();
    if (initialValueRef.current) restore(initialValueRef.current);
    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    pad.addEventListener('endStroke', handleStrokeEnd);

    return () => {
      observer.disconnect();
      pad.removeEventListener('endStroke', handleStrokeEnd);
      pad.off();
      padRef.current = null;
    };
  }, [emitFile]);

  const clearSignature = () => {
    strokesRef.current = [];
    restoredImageRef.current = null;
    padRef.current?.clear();
    emitEmpty();
  };

  const switchMode = (next: SignatureMode) => {
    if (next === mode) return;
    modeRef.current = next;
    typedNameRef.current = '';
    setMode(next);
    setTypedName('');
    clearSignature();
    if (next === 'draw') padRef.current?.on();
    else padRef.current?.off();
  };

  const handleTypedName = (value: string) => {
    const canvas = canvasRef.current;
    setTypedName(value);
    typedNameRef.current = value;
    padRef.current?.clear();
    if (!canvas || !value.trim()) {
      emitEmpty();
      return;
    }
    drawTypedName(canvas, value.trim());
    emitFile();
  };

  const isDrawing = mode === 'draw';
  const tabsIdPrefix = `${id}-signature`;

  return (
    <div className="mt-1 flex flex-col gap-3">
      <TabPills
        ariaLabel="Forma de firmar"
        size="sm"
        idPrefix={tabsIdPrefix}
        tabs={MODE_TABS}
        activeId={mode}
        onSelect={switchMode}
      />

      <div
        role="tabpanel"
        id={tabPanelId(tabsIdPrefix)}
        aria-labelledby={tabPillId(tabsIdPrefix, mode)}
        className="flex flex-col gap-2"
      >
        {!isDrawing && (
          <Input
            ref={focusRef}
            id={id}
            type="text"
            value={typedName}
            onChange={(event) => handleTypedName(event.target.value)}
            placeholder="Escribe tu nombre y apellido"
            autoComplete="name"
            hasError={hasError}
            aria-describedby={describedBy}
          />
        )}

        <div
          ref={isDrawing ? focusRef : undefined}
          id={isDrawing ? id : undefined}
          role={isDrawing ? 'group' : undefined}
          aria-label={isDrawing ? 'Firma dibujada' : undefined}
          aria-invalid={isDrawing && hasError ? 'true' : undefined}
          tabIndex={isDrawing ? -1 : undefined}
          aria-describedby={isDrawing ? describedBy : undefined}
          className={cn(
            'rounded-adipa-md bg-adipa-surface focus-visible:ring-adipa-focus-ring relative overflow-hidden border focus-visible:outline-none focus-visible:ring-[3px]',
            hasError ? 'border-adipa-error' : 'border-adipa-border',
            isDrawing && 'cursor-crosshair',
          )}
        >
          <canvas
            ref={canvasRef}
            role="img"
            aria-label={isDrawing ? 'Área para dibujar tu firma' : 'Vista previa de tu firma'}
            className={cn(
              'bg-adipa-surface text-adipa-text-primary block h-40 w-full',
              isDrawing && 'touch-none',
            )}
          />
          {isDrawing && isEmpty && (
            <span
              aria-hidden="true"
              className="text-adipa-text-placeholder pointer-events-none absolute inset-x-0 bottom-3 text-center text-xs"
            >
              Dibuja tu firma aquí con el dedo o el mouse
            </span>
          )}
          {isDrawing && !isEmpty && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={clearSignature}
              className="absolute right-2 top-2"
            >
              Borrar
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
